import { HugeiconsIcon } from "@hugeicons/react";
import { TextItalicIcon } from "@hugeicons-pro/core-duotone-rounded";
import {
	CodeIcon,
	Heading01Icon,
	Heading02Icon,
	Heading03Icon,
	Heading04Icon,
	Heading05Icon,
	Heading06Icon,
	LeftToRightListDashIcon,
	LeftToRightListNumberIcon,
	LinkIcon,
	ParagraphIcon,
	QuoteDownIcon,
	SourceCodeIcon,
	TextBoldIcon,
	TextStrikethroughIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { type ComponentProps, useEffect } from "react";
import { cn } from "~/lib/utils";
import { Button } from "./button";
import { useFormField } from "./form";

export function FormEditor({
	afterSync,
	watch,
	...props
}: Omit<ComponentProps<typeof EditorContent>, "editor"> & {
	afterSync?: () => void;
	watch?: boolean;
}) {
	const field = useFormField();

	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				link: {
					openOnClick: false,
					autolink: true,
					enableClickSelection: true,
					protocols: ["http", "https"],
					HTMLAttributes: {
						class: "cursor-pointer",
					},
				},
			}),
		],
		editorProps: {
			attributes: {
				class: cn(
					"max-w-full max-h-full overflow-auto max-h-[340px] focus:outline-none",
				),
			},
		},
		content: field.state.value,
		editable: !props.disabled,
		onBlur: field.handleBlur,
		immediatelyRender: false,
		onUpdate(props) {
			field.handleChange(props.editor.getHTML());
		},
	});

	const editorState = useEditorState({
		editor,
		selector: (ctx) => {
			return {
				isBold: ctx.editor?.isActive("bold") ?? false,
				canBold: ctx.editor?.can().chain().toggleBold().run() ?? false,
				isItalic: ctx.editor?.isActive("italic") ?? false,
				canItalic: ctx.editor?.can().chain().toggleItalic().run() ?? false,
				isStrike: ctx.editor?.isActive("strike") ?? false,
				canStrike: ctx.editor?.can().chain().toggleStrike().run() ?? false,
				isCode: ctx.editor?.isActive("code") ?? false,
				canCode: ctx.editor?.can().chain().toggleCode().run() ?? false,
				canClearMarks: ctx.editor?.can().chain().unsetAllMarks().run() ?? false,
				isParagraph: ctx.editor?.isActive("paragraph") ?? false,
				isHeading1: ctx.editor?.isActive("heading", { level: 1 }) ?? false,
				isHeading2: ctx.editor?.isActive("heading", { level: 2 }) ?? false,
				isHeading3: ctx.editor?.isActive("heading", { level: 3 }) ?? false,
				isHeading4: ctx.editor?.isActive("heading", { level: 4 }) ?? false,
				isHeading5: ctx.editor?.isActive("heading", { level: 5 }) ?? false,
				isHeading6: ctx.editor?.isActive("heading", { level: 6 }) ?? false,
				isBulletList: ctx.editor?.isActive("bulletList") ?? false,
				isOrderedList: ctx.editor?.isActive("orderedList") ?? false,
				isCodeBlock: ctx.editor?.isActive("codeBlock") ?? false,
				isBlockquote: ctx.editor?.isActive("blockquote") ?? false,
				isLink: ctx.editor?.isActive("link") ?? false,
				canUndo: ctx.editor?.can().chain().undo().run() ?? false,
				canRedo: ctx.editor?.can().chain().redo().run() ?? false,
			};
		},
	});

	useEffect(() => {
		if (!watch || !editor) {
			return;
		}

		if (editor && field.state.value !== editor.getHTML()) {
			editor.commands.setContent(field.state.value || "");
		}

		afterSync?.();
	}, [field.state.value, editor, watch, afterSync]);

	return (
		<div className="border-input placeholder:text-muted-foreground/70 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive min-h-19.5 w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 prose max-w-full h-[400px]">
			<div className="flex flex-wrap items-center gap-2 border-b pb-2">
				<Button
					size="sm"
					onClick={() => editor?.chain().focus().toggleBold().run()}
					disabled={!editorState?.canBold}
					variant={editorState?.isBold ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={TextBoldIcon} />
				</Button>
				<Button
					size="sm"
					onClick={() => editor?.chain().focus().toggleItalic().run()}
					disabled={!editorState?.canItalic}
					variant={editorState?.isItalic ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={TextItalicIcon} />
				</Button>
				<Button
					size="sm"
					onClick={() => editor?.chain().focus().toggleStrike().run()}
					disabled={!editorState?.canStrike}
					variant={editorState?.isStrike ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={TextStrikethroughIcon} />
				</Button>
				<Button
					size="sm"
					onClick={() => editor?.chain().focus().toggleCode().run()}
					disabled={!editorState?.canCode}
					variant={editorState?.isCode ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={CodeIcon} />
				</Button>
				<div className="border-l h-4" />
				<Button
					size="sm"
					onClick={() => editor?.chain().focus().setParagraph().run()}
					variant={editorState?.isParagraph ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={ParagraphIcon} />
				</Button>
				<Button
					size="sm"
					onClick={() =>
						editor?.chain().focus().toggleHeading({ level: 1 }).run()
					}
					variant={editorState?.isHeading1 ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={Heading01Icon} />
				</Button>
				<Button
					size="sm"
					onClick={() =>
						editor?.chain().focus().toggleHeading({ level: 2 }).run()
					}
					variant={editorState?.isHeading2 ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={Heading02Icon} />
				</Button>
				<Button
					size="sm"
					onClick={() =>
						editor?.chain().focus().toggleHeading({ level: 3 }).run()
					}
					variant={editorState?.isHeading3 ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={Heading03Icon} />
				</Button>
				<Button
					size="sm"
					onClick={() =>
						editor?.chain().focus().toggleHeading({ level: 4 }).run()
					}
					variant={editorState?.isHeading4 ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={Heading04Icon} />
				</Button>
				<Button
					size="sm"
					onClick={() =>
						editor?.chain().focus().toggleHeading({ level: 5 }).run()
					}
					variant={editorState?.isHeading5 ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={Heading05Icon} />
				</Button>
				<Button
					size="sm"
					onClick={() =>
						editor?.chain().focus().toggleHeading({ level: 6 }).run()
					}
					variant={editorState?.isHeading6 ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={Heading06Icon} />
				</Button>
				<div className="border-l h-4" />
				<Button
					size="sm"
					onClick={() => editor?.chain().focus().toggleBulletList().run()}
					variant={editorState?.isBulletList ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={LeftToRightListDashIcon} />
				</Button>
				<Button
					size="sm"
					onClick={() => editor?.chain().focus().toggleOrderedList().run()}
					variant={editorState?.isOrderedList ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={LeftToRightListNumberIcon} />
				</Button>
				<Button
					size="sm"
					onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
					variant={editorState?.isCodeBlock ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={SourceCodeIcon} />
				</Button>
				<Button
					size="sm"
					onClick={() => editor?.chain().focus().toggleBlockquote().run()}
					variant={editorState?.isBlockquote ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={QuoteDownIcon} />
				</Button>
				<Button
					size="sm"
					onClick={() => {
						const previousUrl = editor?.getAttributes("link").href;
						const url = window.prompt("URL", previousUrl);

						// cancelled
						if (url === null) {
							return;
						}

						// empty
						if (url === "") {
							editor?.chain().focus().extendMarkRange("link").unsetLink().run();

							return;
						}

						// update link
						try {
							editor
								?.chain()
								.focus()
								.extendMarkRange("link")
								.setLink({ href: url })
								.run();
						} catch (e: any) {
							alert(e.message);
						}
					}}
					variant={editorState?.isLink ? "secondary" : "ghost"}
				>
					<HugeiconsIcon icon={LinkIcon} />
				</Button>
			</div>
			<EditorContent
				editor={editor}
				value={field.state.value}
				disabled
				{...props}
			/>
		</div>
	);
}
